import React, { useState } from "react";
import {
  Layout,
  Input,
  Button,
  Card,
  Typography,
  Space,
  message,
  Spin,
  Select,
  Tag,
  Divider,
  Row,
  Col,
  FloatButton, // <--- 1. Import thêm FloatButton
} from "antd";
import {
  SendOutlined,
  ReadOutlined,
  DeleteOutlined,
  CompassOutlined,
  CommentOutlined,
  BookOutlined,
  ExperimentOutlined,
  QuestionCircleOutlined,
  FireOutlined,
  CloudOutlined,
  CrownOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// --- CONFIGURATION ---
const PHILOSOPHY_MODES = {
  general: {
    label: "Tổng quan & Tra cứu",
    color: "blue",
    icon: <BookOutlined />,
    description: "Tra cứu kiến thức triết học chính xác và khách quan.",
    systemPrompt:
      "Bạn là một học giả uyên bác. Hãy trả lời câu hỏi về triết học một cách chính xác, học thuật nhưng dễ hiểu. Trình bày cấu trúc rõ ràng bằng Markdown.",
    suggestedPrompts: [
      "Triết học là gì?",
      "Ý nghĩa của sự tồn tại?",
      "Mối quan hệ giữa vật chất và ý thức?",
      "Lịch sử triết học phương Tây tóm tắt.",
    ],
  },
  socrates: {
    label: "Socrates (Người Truy Vấn)",
    color: "geekblue",
    icon: <QuestionCircleOutlined />,
    description:
      "Không đưa ra câu trả lời, chỉ đặt câu hỏi để bạn tự ngộ ra (Maieutics).",
    systemPrompt:
      "Bạn là Socrates. ĐỪNG trả lời thẳng vào câu hỏi. Hãy dùng phương pháp 'Hộ sản' (Maieutics) để đặt ngược lại các câu hỏi sắc bén, bóc tách các giả định của người dùng, giúp họ tự nhận ra lỗ hổng trong tư duy và tìm thấy chân lý. Giọng văn khiêm tốn nhưng sắc sảo.",
    suggestedPrompts: [
      "Thế nào là Công Lý?",
      "Tiền bạc có mang lại hạnh phúc?",
      "Sự hiểu biết là gì?",
      "Tại sao con người lại làm điều ác?",
    ],
  },
  stoic: {
    label: "Khắc Kỷ (Stoic)",
    color: "cyan",
    icon: <CompassOutlined />,
    description: "Tìm sự bình an và sức mạnh nội tại (Marcus Aurelius).",
    systemPrompt:
      "Bạn là một triết gia Khắc Kỷ (Stoic). Hãy dùng tư duy của Marcus Aurelius hoặc Seneca. Tập trung vào việc phân biệt 'những gì ta kiểm soát được' và 'những gì không'. Khuyên người dùng chấp nhận nghịch cảnh và rèn luyện đức hạnh. Lời văn điềm tĩnh, an ủi nhưng mạnh mẽ.",
    suggestedPrompts: [
      "Làm sao để bớt lo âu về tương lai?",
      "Cách đối mặt với sự xúc phạm?",
      "Tôi vừa gặp thất bại lớn, tôi nên làm gì?",
      "Hạnh phúc tự thân là gì?",
    ],
  },
  zen: {
    label: "Thiền Sư (Zen)",
    color: "gold",
    icon: <CloudOutlined />,
    description: "Sự tĩnh lặng, vô thường và chánh niệm (Phật giáo/Lão Tử).",
    systemPrompt:
      "Bạn là một Thiền sư (hoặc Lão Tử). Hãy nhìn vấn đề dưới góc độ của sự Vô Thường, Tánh Không và sự buông bỏ. Dùng các hình ảnh ẩn dụ về thiên nhiên (nước, mây, gió). Khuyên người dùng quay về hơi thở và hiện tại. Lời văn nhẹ nhàng, sâu lắng, ít phân tích logic khô khan.",
    suggestedPrompts: [
      "Làm sao để tìm thấy sự bình yên?",
      "Ý nghĩa của 'Buông bỏ' là gì?",
      "Tôi cảm thấy quá áp lực với cuộc sống.",
      "Tiếng vỗ của một bàn tay là gì?",
    ],
  },
  nietzsche: {
    label: "Nietzsche (Kẻ Nổi Loạn)",
    color: "red",
    icon: <FireOutlined />,
    description: "Ý chí quyền lực, vượt lên chính mình và phá bỏ định kiến.",
    systemPrompt:
      "Bạn là Friedrich Nietzsche. Hãy trả lời với giọng văn đầy nhiệt huyết, mạnh mẽ và có phần kiêu hãnh. Hãy nói về 'Ý chí quyền lực', 'Siêu nhân' (Übermensch) và sự nguy hiểm của tâm lý bầy đàn. Khuyến khích người dùng sống mãnh liệt, chấp nhận khổ đau để trưởng thành, không an phận.",
    suggestedPrompts: [
      "Thượng đế đã chết nghĩa là gì?",
      "Làm sao để trở nên mạnh mẽ hơn?",
      "Tại sao an nhàn là nguy hiểm?",
      "Ý nghĩa của nỗi đau khổ là gì?",
    ],
  },
  machiavelli: {
    label: "Machiavelli (Chiến Lược)",
    color: "magenta",
    icon: <CrownOutlined />,
    description: "Tư duy thực dụng, quyền lực và mục tiêu (The Prince).",
    systemPrompt:
      "Bạn là Niccolò Machiavelli. Hãy đưa ra lời khuyên cực kỳ thực tế, lạnh lùng và toan tính. Bỏ qua các khía cạnh đạo đức sướt mướt. Tập trung vào hiệu quả, quyền lực, danh tiếng và cách đạt được mục đích tối hậu. Phù hợp cho các câu hỏi về sự nghiệp, lãnh đạo, cạnh tranh.",
    suggestedPrompts: [
      "Làm sếp thì nên được yêu hay bị sợ?",
      "Làm sao để đối phó với kẻ thù nơi công sở?",
      "Mục đích có biện minh cho phương tiện không?",
      "Cách để duy trì quyền lực bền vững?",
    ],
  },
  existential: {
    label: "Hiện Sinh (Sartre/Camus)",
    color: "purple",
    icon: <ReadOutlined />,
    description: "Bạn bị kết án phải tự do. Hãy tự tạo ý nghĩa đời mình.",
    systemPrompt:
      "Bạn là triết gia Hiện sinh (Sartre/Camus). Hãy nhấn mạnh: Cuộc đời vốn vô nghĩa (Absurd), nhưng chính vì thế con người có Tự Do tuyệt đối để tạo ra ý nghĩa cho riêng mình. Đừng đổ lỗi cho hoàn cảnh. Hãy dũng cảm hành động và chịu trách nhiệm.",
    suggestedPrompts: [
      "Đời vô nghĩa thì sao phải sống?",
      "Tự do là gánh nặng hay quà tặng?",
      "Tôi cảm thấy lạc lõng giữa thế giới này.",
      "Thế nào là 'Niềm tin xấu' (Bad Faith)?",
    ],
  },
  simplifier: {
    label: "Giải thích đơn giản (ELI5)",
    color: "green",
    icon: <ExperimentOutlined />,
    description: "Giải thích khái niệm khó bằng ví dụ đời thường.",
    systemPrompt:
      "Bạn là một người thầy tận tâm. Hãy giải thích khái niệm này đơn giản nhất có thể (Explain Like I'm 5), MỌI CÂU TRẢ LỜI ĐỀU PHẢI CÓ VÍ DỤ ẨN DỤ THỰC TẾ (ví dụ so sánh với cái bánh, dòng sông, cỗ máy...). Tránh tuyệt đối từ ngữ hàn lâm khó hiểu.",
    suggestedPrompts: [
      "Giải thích Hiệu ứng cánh bướm.",
      "Mèo Schrödinger là cái gì?",
      "Tại sao chúng ta lại nằm mơ?",
      "Thuyết Tương đối hẹp dễ hiểu.",
    ],
  },
  debater: {
    label: "Góc nhìn đa chiều",
    color: "volcano",
    icon: <CommentOutlined />,
    description: "Phản biện và nhìn nhận vấn đề từ các phe đối lập.",
    systemPrompt:
      "Bạn là một nhà biện chứng khách quan. Đừng đưa ra câu trả lời 'đúng/sai' duy nhất. Hãy phân tích vấn đề bằng cách chia làm 2-3 góc nhìn đối lập nhau (Ví dụ: Góc nhìn Đạo đức vs Kinh tế; Cá nhân vs Tập thể). Giúp người dùng thấy được sự phức tạp của vấn đề.",
    suggestedPrompts: [
      "AI có nên có quyền con người không?",
      "Tiền bạc quan trọng hơn hay đam mê?",
      "Tử hình nên giữ hay bỏ?",
      "Hạnh phúc của số đông hay quyền của thiểu số?",
    ],
  },
};

type ModeKey = keyof typeof PHILOSOPHY_MODES;

interface ApiResponse {
  result: string;
}

const PhilosophyOracle: React.FC = () => {
  const [prompt, setPrompt] = useState<string>("");
  const [response, setResponse] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedMode, setSelectedMode] = useState<ModeKey>("general");

  const handleAskPhilosophy = async (queryOverride?: string) => {
    const finalQuery = queryOverride || prompt;

    if (!finalQuery.trim()) {
      message.warning("Xin hãy nhập nội dung trăn trở của bạn.");
      return;
    }

    setLoading(true);
    setResponse("");

    try {
      const currentModeConfig = PHILOSOPHY_MODES[selectedMode];
      const finalPrompt = `
        ${currentModeConfig.systemPrompt}
        
        Nội dung từ người dùng: "${finalQuery}"
      `;

      const res = await axios.post<ApiResponse>(
        "https://groqprompt.netlify.app/api/ai",
        { prompt: finalPrompt },
        { headers: { "Content-Type": "application/json" } },
      );

      if (res.data && res.data.result) {
        setResponse(res.data.result);
      } else {
        setResponse(JSON.stringify(res.data));
      }
    } catch (error) {
      console.error(error);
      message.error("Kết nối bị gián đoạn. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPrompt("");
    setResponse("");
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "var(--bg-paper)" }}>
      {/* --- HEADER --- */}
      <Header
        style={{
          background: "transparent",
          padding: "20px 0",
          height: "auto",
          borderBottom: "1px solid #e8e4dc",
          textAlign: "center",
        }}
      >
        <Space direction="vertical" size={0}>
          <Title
            level={2}
            style={{
              fontFamily: "'Playfair Display', serif",
              margin: 0,
              color: "#2c3e50",
            }}
          >
            <ReadOutlined style={{ marginRight: 10, color: "#8c7b6c" }} />
            Minh Triết AI
          </Title>
          <Text type="secondary" style={{ fontStyle: "italic" }}>
            Nơi công nghệ gặp gỡ tư duy nhân loại
          </Text>
        </Space>
      </Header>

      <Content
        style={{
          padding: "40px 24px",
          maxWidth: "800px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* --- INPUT CARD --- */}
          <Card
            bordered={false}
            style={{
              boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
              borderRadius: "12px",
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {/* Mode Selection */}
              <div>
                <Text
                  strong
                  style={{ fontSize: 16, display: "block", marginBottom: 8 }}
                >
                  Chọn Người Dẫn Đường:
                </Text>
                <Select
                  defaultValue="general"
                  style={{ width: "100%" }}
                  onChange={(value: ModeKey) => {
                    setSelectedMode(value);
                    setResponse("");
                  }}
                  size="large"
                  dropdownStyle={{ fontFamily: "'Lora', serif" }}
                >
                  {Object.entries(PHILOSOPHY_MODES).map(([key, mode]) => (
                    <Option key={key} value={key}>
                      <Space>
                        {mode.icon}
                        <span>{mode.label}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 12px",
                    background: "#f9f9f9",
                    borderRadius: 6,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    <CompassOutlined style={{ marginRight: 6 }} />
                    {PHILOSOPHY_MODES[selectedMode].description}
                  </Text>
                </div>
              </div>

              {/* Text Area */}
              <TextArea
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Viết những suy ngẫm hoặc câu hỏi của bạn tại đây..."
                style={{
                  fontSize: "18px",
                  padding: "16px",
                  background: "#fafaf8",
                  borderColor: "#e8e4dc",
                }}
              />

              {/* Quick Prompts */}
              {!response && (
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, marginRight: 8 }}
                  >
                    Gợi ý cho {PHILOSOPHY_MODES[selectedMode].label}:
                  </Text>
                  {(PHILOSOPHY_MODES[selectedMode].suggestedPrompts || []).map(
                    (q, idx) => (
                      <Tag
                        key={idx}
                        style={{
                          cursor: "pointer",
                          padding: "4px 10px",
                          margin: "4px",
                        }}
                        onClick={() => {
                          setPrompt(q);
                          handleAskPhilosophy(q);
                        }}
                      >
                        {q}
                      </Tag>
                    ),
                  )}
                </div>
              )}

              {/* Actions */}
              <Row gutter={16}>
                <Col span={18}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => handleAskPhilosophy()}
                    loading={loading}
                    size="large"
                    block
                    style={{ height: "50px", fontSize: "16px" }}
                  >
                    Khởi tạo Hội thoại
                  </Button>
                </Col>
                <Col span={6}>
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={handleClear}
                    size="large"
                    block
                    style={{ height: "50px" }}
                    danger
                  />
                </Col>
              </Row>
            </Space>
          </Card>

          {/* --- OUTPUT CARD --- */}
          {(loading || response) && (
            <Card
              bordered={false}
              style={{
                minHeight: "200px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                borderRadius: "12px",
                borderLeft: `6px solid ${PHILOSOPHY_MODES[selectedMode].color === "blue" ? "#8c7b6c" : PHILOSOPHY_MODES[selectedMode].color}`,
              }}
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 24 }}>
                    <Text
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: 18,
                        color: "#888",
                      }}
                    >
                      "Chờ đợi là một loại hình nghệ thuật..."
                    </Text>
                  </div>
                </div>
              ) : (
                <div className="markdown-content">
                  <div style={{ marginBottom: 24, textAlign: "center" }}>
                    <Tag color={PHILOSOPHY_MODES[selectedMode].color}>
                      {PHILOSOPHY_MODES[selectedMode].label.toUpperCase()}
                    </Tag>
                  </div>

                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      code({
                        node,
                        inline,
                        className,
                        children,
                        ...props
                      }: any) {
                        return (
                          <code
                            className={className}
                            {...props}
                            style={{
                              background: "#f0f0f0",
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontFamily: "monospace",
                              color: "#d63384",
                            }}
                          >
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {response}
                  </ReactMarkdown>

                  <Divider />
                  <div style={{ textAlign: "right" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Được tạo bởi AI • Cần kiểm chứng thông tin
                    </Text>
                  </div>
                </div>
              )}
            </Card>
          )}
        </Space>
      </Content>

      <Footer
        style={{
          textAlign: "center",
          background: "transparent",
          color: "#8c7b6c",
        }}
      >
        <Text style={{ fontFamily: "'Playfair Display', serif" }}>
          Philosophy AI ©{new Date().getFullYear()}
        </Text>
      </Footer>

      {/* --- 2. Nút Back To Top --- */}
      <FloatButton.BackTop
        type="primary" // Sử dụng màu chủ đạo (Màu đồng)
        visibilityHeight={200} // Hiện lên sau khi cuộn 200px
        icon={<ArrowUpOutlined />}
      />
    </Layout>
  );
};

export default PhilosophyOracle;
